import {
  SET_PAGE,
  SET_BID_STAGING_EQUIPMENT,
  SET_BID_STAGING_DEVICES,
  SET_BID_STAGING_POINTS,
  MARK_STAGING_LOADING,
  SET_VALIDATION,
  UPDATE_STAGING_EQUIP,
  UPDATE_STAGING_POINTS,
  DELETE_STAGING_EQUIPMENT,
} from './actiontypes';
import {
  deleteStagingEquipment,
  getBuildingStagingEquipment,
  getBuildingStagingDevices,
  getBuildingStagingPoints,
  getBuildingStagingPointsByTopics,
  promoteStagingEquipment,
  validateStaging,
  getEquipmentStaging,
  patchStagingPointUpdate,
  patchStagingEquipUpdate,
  postNewStagingEquip,
  demote,
} from '../../api/Api';
import { fetchEquipForBuildingActionCreator, removeMultipleEquipmentAction } from './equipment';
import { fetchLocationsForBuildingAction } from './buildings';

export const setPage = (pageNumber) => (dispatch) =>
  dispatch({
    type: SET_PAGE,
    newPage: pageNumber,
  });

const setBuildingStagingEquipment = (dispatch, buildingId, equipment) =>
  dispatch({
    type: SET_BID_STAGING_EQUIPMENT,
    buildingId,
    equipment,
  });

const setBuildingStagingDevices = (dispatch, buildingId, devices) =>
  dispatch({
    type: SET_BID_STAGING_DEVICES,
    buildingId,
    devices,
  });

const setBuildingStagingPoints = (dispatch, buildingId, points) =>
  dispatch({
    type: SET_BID_STAGING_POINTS,
    buildingId,
    points,
  });

const dispatchStagingEquipmentUpdate = (dispatch, buildingId, equipment) =>
  dispatch({
    type: UPDATE_STAGING_EQUIP,
    buildingId,
    equipment,
  });

const dispatchStagingPointsUpdate = (dispatch, buildingId, points) =>
  dispatch({
    type: UPDATE_STAGING_POINTS,
    buildingId,
    points,
  });

export const validateStagingAction = (buildingId) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return validateStaging(jwt, buildingId)
    .then((data) => {
      const { equipment, points } = data;
      dispatch({ type: SET_VALIDATION, buildingId, equipment, points, buildingError: {} });
      return data;
    })
    .catch((buildingError) => {
      dispatch({ type: SET_VALIDATION, buildingId, buildingError });
    });
};

export const fetchStagingAction = (buildingId) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  dispatch({ type: MARK_STAGING_LOADING, buildingId });
  const deviceUpdate = getBuildingStagingDevices(jwt, buildingId).then((devices) =>
    setBuildingStagingDevices(dispatch, buildingId, devices)
  );
  const equipUpdate = getBuildingStagingEquipment(jwt, buildingId).then((equipment) =>
    setBuildingStagingEquipment(dispatch, buildingId, equipment)
  );
  const pointsFuture = getBuildingStagingPoints(jwt, buildingId);
  // WE want to be sure device & equip dispatch before points
  return Promise.all([deviceUpdate, equipUpdate])
    .then(() =>
      pointsFuture.then((points) => setBuildingStagingPoints(dispatch, buildingId, points))
    )
    .then((result) => {
      // We do not want the validate to be blocking
      dispatch(validateStagingAction(buildingId, true));
      return result;
    });
};

export const getPointDetailsAction = (buildingId, topics) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  if (topics.length === 0) return () => {};
  const chunkSize = 150;
  const promises = [];
  for (let i = 0; i < topics.length; i += chunkSize) {
    const chunk = topics.slice(i, i + chunkSize);
    promises.push(
      getBuildingStagingPointsByTopics(jwt, buildingId, chunk).then((points) =>
        dispatchStagingPointsUpdate(dispatch, buildingId, points)
      )
    );
  }
  return Promise.all(promises);
};

export const getEquipmentDetailsAction = (buildingId, equipNames) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  const promises = equipNames.map((name) => getEquipmentStaging(jwt, buildingId, name));
  return Promise.all(promises)
    .then((results) => results.flat())
    .then((equipment) => dispatchStagingEquipmentUpdate(dispatch, buildingId, equipment));
};

export const promoteEquipsTopicsAction =
  (buildingId, equipmentList, topics) => (dispatch, getState) => {
    const {
      user: { jwt },
    } = getState();
    return promoteStagingEquipment(
      jwt,
      buildingId,
      equipmentList.map((e) => e.equip_id),
      topics
    ).then((data) => {
      const { equipment, points } = data;
      return Promise.all([
        dispatch({ type: SET_VALIDATION, buildingId, equipment, points }),
        dispatch(getPointDetailsAction(buildingId, topics)),
        dispatch(
          getEquipmentDetailsAction(
            buildingId,
            equipmentList.map((e) => e.name)
          )
        ),
        dispatch(fetchEquipForBuildingActionCreator(buildingId, true, true, jwt, true)),
      ]).then(() => data);
    });
  };

export const updateStagingPoints = (buildingId, pointsUpdates) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return patchStagingPointUpdate(jwt, buildingId, pointsUpdates)
    .then((points) => dispatchStagingPointsUpdate(dispatch, buildingId, points))
    .then((result) => {
      // We do not want the validate to be blocking
      dispatch(validateStagingAction(buildingId, true));
      return result;
    });
};

export const createStagingEquipment = (buildingId, name) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return postNewStagingEquip(jwt, buildingId, name)
    .then((equipment) => dispatchStagingEquipmentUpdate(dispatch, buildingId, [equipment]))
    .then((result) => {
      // We do not want the validate to be blocking
      dispatch(validateStagingAction(buildingId, true));
      return result;
    });
};

export const updateStagingEquipment = (buildingId, equipUpdates) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return patchStagingEquipUpdate(jwt, buildingId, equipUpdates)
    .then((equipment) => dispatchStagingEquipmentUpdate(dispatch, buildingId, equipment))
    .then((result) => {
      // We do not want the validate to be blocking
      dispatch(validateStagingAction(buildingId, true));
      return result;
    });
};

/**
 * Demote points, equipment, and/or point-equipment relationships. This does not do any state refreshes as it's currently expected to be used in conjunction with other operations against the specified points
 */
export const demoteStaging = (buildingId, demotePayload) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return demote(jwt, buildingId, { ...demotePayload }).then((result) =>
    Promise.all([
      dispatch(removeMultipleEquipmentAction(buildingId, result.equipment_deleted)),
      // Need to refresh equipment to ensure relationships refresh
      dispatch(fetchEquipForBuildingActionCreator(buildingId, true, true, jwt, true)),
      // Need to refresh points to updjte demoted points
      getBuildingStagingPoints(jwt, buildingId).then((points) => {
        setBuildingStagingPoints(dispatch, buildingId, points);
      }),
      // Need to refresh locations in case demoted equipment belonged to locations(s)
      dispatch(fetchLocationsForBuildingAction(buildingId, true)),
    ])
  );
};

/**
 * Delete an empty staging equipment
 */
export const stagingEquipmentDelete = (buildingId, equipIds) => (dispatch, getState) => {
  const {
    user: { jwt },
  } = getState();
  return deleteStagingEquipment(jwt, buildingId, equipIds).then((response) => {
    dispatch({
      type: DELETE_STAGING_EQUIPMENT,
      buildingId,
      equipIds: response.equipment_deleted,
    });
    return equipIds;
  });
};
